import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { FragranceInputSection } from '../components/FragranceModule';
import { ClaimSelectionSection } from '../components/ClaimsModule';
import { SiteHeader } from '../components/SiteLayout';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  Flask, 
  UploadSimple, 
  File,
  X,
  ArrowLeft,
  Plus,
  Trash,
  SpinnerGap,
  Lightning,
  Microscope,
  Info,
  Drop,
  Sparkle,
  Eyeglasses,
  Baby,
  SunDim,
  ShieldCheck,
  Broom,
  Cookie,
  Wind,
  Pill,
  Question,
  HandSoap,
  HairDryer,
  Flask as FlaskIcon,
  Shower,
  Flower,
  CheckCircle,
  Warning,
  WarningCircle,
  PawPrint,
  Calculator,
  Scales,
  FileDoc,
  Certificate
} from '@phosphor-icons/react';
import TI from '../components/brand/TI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Product category icon mapping
const CATEGORY_ICONS = {
  face_cream: Drop, body_lotion: Drop, hand_cream: Drop,
  lip_product: Sparkle, eye_product: Eyeglasses,
  fine_fragrance: Flower, body_mist: Flower,
  hair_styling: HairDryer,
  shampoo: Shower, conditioner: Shower, cleanser: HandSoap, body_wash: Shower,
  baby_product: Baby,
  sunscreen: SunDim, deodorant: ShieldCheck, oral_care: Sparkle,
  laundry_detergent: Broom, fabric_softener: Broom,
  surface_cleaner: Broom, dish_soap: HandSoap, air_freshener: Wind,
  ingestible: Pill, custom: Question,
  pet_shampoo: PawPrint, pet_conditioner: PawPrint,
  pet_spray_leave_on: PawPrint, pet_grooming_wipe: PawPrint,
  pet_dental: PawPrint, pet_ear_cleaner: PawPrint,
};

// Live Hazard Preview panel — debounces calls to /api/formulations/screen-preview
function LiveHazardPreview({ ingredients }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef(null);
  const cancelRef = useRef(null);

  // Only send rows with an INCI name + concentration
  const validRows = useMemo(
    () => ingredients
      .filter(i => i.inci_name.trim() && i.concentration)
      .map(i => ({
        inci_name: i.inci_name.trim(),
        cas_number: i.cas_number?.trim() || null,
        concentration: parseFloat(i.concentration) || 0,
        function: i.function?.trim() || null,
      })),
    [ingredients]
  );

  const rowsKey = useMemo(() => JSON.stringify(validRows), [validRows]);

  useEffect(() => {
    if (validRows.length === 0) { setData(null); setError(''); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (cancelRef.current) cancelRef.current.cancel('superseded');
      const source = axios.CancelToken.source();
      cancelRef.current = source;
      setLoading(true); setError('');
      try {
        const resp = await axios.post(
          `${API_URL}/api/formulations/screen-preview`,
          { ingredients: validRows },
          { withCredentials: true, cancelToken: source.token }
        );
        setData(resp.data);
      } catch (e) {
        if (!axios.isCancel(e)) setError('Preview unavailable');
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsKey]);

  if (validRows.length === 0) return null;

  const statusMeta = {
    acceptable:     { cls: 'text-[#0E9F6E] border-[#31C48D] bg-[#DEF7EC]', label: 'ACCEPTABLE', Icon: CheckCircle },
    needs_review:   { cls: 'text-[#D97706] border-[#F3CC64] bg-[#FEF3C7]', label: 'NEEDS REVIEW', Icon: Warning },
    not_acceptable: { cls: 'text-[#DC2626] border-[#F8B4B4] bg-[#FDE8E8]', label: 'NOT ACCEPTABLE', Icon: WarningCircle },
  };
  const meta = data ? (statusMeta[data.overall_status] || statusMeta.needs_review) : null;

  return (
    <div className="mt-5 border border-dashed border-gray-300 rounded-sm p-4 bg-[#F9FAFB]" data-testid="live-hazard-preview-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightning size={18} className="text-[#2563EB]" weight="duotone" />
          <h3 className="text-sm font-bold text-[#111827]">Live Hazard Preview</h3>
          <span className="text-[10px] font-mono uppercase tracking-wider text-[#4B5563]">debounced · real-time</span>
        </div>
        {loading && <SpinnerGap size={16} className="text-[#2563EB] animate-spin" data-testid="live-preview-loading" />}
      </div>

      {error && <p className="text-xs text-[#9B1C1C]">{error}</p>}

      {data && meta && (
        <>
          <div className={`flex items-center gap-2 px-3 py-2 border rounded-sm mb-3 ${meta.cls}`} data-testid="live-preview-status">
            <meta.Icon size={16} weight="fill" />
            <span className="text-xs font-bold tracking-wider">{meta.label}</span>
            <span className="text-xs ml-auto tabular-nums">{data.total_ingredients} ingredient{data.total_ingredients !== 1 ? 's' : ''}</span>
          </div>

          {/* Flag chips */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {Object.entries(data.hazard_flags).filter(([, v]) => v > 0).map(([k, v]) => {
              const labels = {
                genotoxicity: 'Genotoxic',
                mutagenicity: 'Mutagenic',
                eu_annex_ii: 'EU Banned',
                eu_annex_iii_exceeded: 'EU Limit Exceeded',
                ifra_restricted: 'IFRA Restricted',
                prop_65: 'Prop 65',
                unknown_ingredient: 'Unknown',
                strong_sensitizer: 'Strong Sensitizer',
              };
              const severe = ['genotoxicity', 'mutagenicity', 'eu_annex_ii', 'eu_annex_iii_exceeded'].includes(k);
              return (
                <span key={k}
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded border tabular-nums ${
                    severe
                      ? 'bg-[#FDE8E8] border-[#F8B4B4] text-[#9B1C1C]'
                      : 'bg-[#FEF3C7] border-[#F3CC64] text-[#723B13]'
                  }`}
                  data-testid={`live-flag-${k}`}>
                  {labels[k] || k}: {v}
                </span>
              );
            })}
            {Object.values(data.hazard_flags).every(v => v === 0) && (
              <span className="text-[10px] text-[#4B5563]">No regulatory flags raised at current concentrations.</span>
            )}
          </div>

          {/* Per-ingredient compact list */}
          <div className="space-y-1 max-h-44 overflow-y-auto">
            {data.results.map((r) => {
              const rMeta = statusMeta[r.risk_level] || statusMeta.needs_review;
              return (
                <div key={`${r.ingredient_name}-${r.concentration}`}
                     className="flex items-center justify-between gap-2 text-xs px-2 py-1 bg-white border border-gray-200 rounded-sm"
                     data-testid={`live-preview-ingredient-${r.ingredient_name}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <rMeta.Icon size={12} weight="fill" className={rMeta.cls.split(' ')[0]} />
                    <span className="font-semibold text-[#111827] truncate">{r.ingredient_name}</span>
                    <span className="text-[#4B5563] font-mono tabular-nums">{r.concentration}%</span>
                  </div>
                  <span className="text-[#4B5563] truncate hidden sm:inline">{r.interpretation}</span>
                </div>
              );
            })}
          </div>

          <p className="text-[10px] text-[#4B5563] mt-3">
            Live preview runs without saving. Submit via the assessment buttons below to persist the formulation and generate a report.
          </p>
        </>
      )}
    </div>
  );
}

export default function UploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [uploadMethod, setUploadMethod] = useState('file');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Product category
  const [categories, setCategories] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [productName, setProductName] = useState('');

  // Custom exposure toggle
  const [useCustomExposure, setUseCustomExposure] = useState(false);
  const [customExposure, setCustomExposure] = useState({
    amount_g: '', frequency: '', retention_factor: '', body_weight: '',
    exposure_route: 'dermal', ingestion_factor: ''
  });

  // Manual entry ingredients
  const [ingredients, setIngredients] = useState([
    { _id: crypto.randomUUID(), inci_name: '', cas_number: '', concentration: '', function: '' }
  ]);

  // Supporting documents
  const [supportDocs, setSupportDocs] = useState([]);

  // Fragrance data
  const [fragrance, setFragrance] = useState({
    contains_fragrance: false,
    fragrance_disclosed: false,
    fragrance_pct_in_product: 1.0,
    fragrance_components: [],
    ifra_certificate_uploaded: false
  });

  // Safety claims
  const [hasClaims, setHasClaims] = useState(false);
  const [safetyClaims, setSafetyClaims] = useState([]);

  // Pet product selectors (used when a Pet Product category is chosen)
  const [petSpecies, setPetSpecies] = useState('dog');
  const [petSize, setPetSize] = useState('medium');

  // Fetch product categories on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/product-categories`).then(res => {
      setCategories(res.data);
    }).catch(err => console.error('Failed to load categories:', err));
  }, []);

  const selectedCatData = categories?.all?.[selectedCategory];

  const handleDrag = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const f = e.dataTransfer.files[0];
      if (f.name.match(/\.(csv|xlsx|xls)$/i)) { setFile(f); setError(''); }
      else setError('Please upload a CSV or Excel file');
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) {
      const f = e.target.files[0];
      if (f.name.match(/\.(csv|xlsx|xls)$/i)) { setFile(f); setError(''); }
      else setError('Please upload a CSV or Excel file');
    }
  };

  const handleSupportDocSelect = (e) => {
    if (e.target.files) {
      setSupportDocs(prev => [...prev, ...Array.from(e.target.files).filter(f => f.size <= 25 * 1024 * 1024)]);
    }
  };

  const removeSupportDoc = (idx) => setSupportDocs(prev => prev.filter((_, i) => i !== idx));

  const addIngredient = () => setIngredients([...ingredients, { _id: crypto.randomUUID(), inci_name: '', cas_number: '', concentration: '', function: '' }]);
  const removeIngredient = (index) => { if (ingredients.length > 1) setIngredients(ingredients.filter((_, i) => i !== index)); };
  const updateIngredient = (index, field, value) => { const u = [...ingredients]; u[index][field] = value; setIngredients(u); };

  const validateBase = (mode) => {
    if (!productName.trim()) { setError('Product name is required'); return false; }
    const exposureRequired = ['full', 'tra_qra', 'prop65'];
    if (exposureRequired.includes(mode) && !selectedCategory) {
      setError(`Please select a product type before running this workflow`); return false;
    }
    if (selectedCategory === 'custom' && exposureRequired.includes(mode) && !useCustomExposure) {
      setError('Custom/Unknown product requires manual exposure parameters. Enable "Customize Exposure Scenario" and fill in values.'); return false;
    }
    if (uploadMethod === 'file' && !file) { setError('Please upload a formulation file'); return false; }
    if (uploadMethod === 'manual') {
      if (ingredients.filter(i => i.inci_name.trim() && i.concentration).length === 0) {
        setError('Add at least one ingredient with name and concentration'); return false;
      }
    }
    return true;
  };

  const buildMetadata = () => {
    const cat = selectedCatData || {};
    const isPet = !!cat.pet_product;
    const meta = {
      product_name: productName,
      product_category: selectedCategory || 'custom',
      product_type: cat.subgroup || 'Unknown',
      target_population: isPet ? 'pet' : (cat.body_weight === 5.0 ? 'baby' : 'adult'),
      application_area: cat.group || 'Unknown',
      frequency_of_use: cat.frequency || 1.0,
      claims: [],
      safety_claims: hasClaims ? safetyClaims : [],
      use_custom_exposure: useCustomExposure,
      is_pet_product: isPet,
      pet_species: isPet ? (petSpecies || cat.default_species || 'dog') : null,
      pet_size: isPet ? (petSize || 'medium') : null,
    };
    if (useCustomExposure) {
      meta.custom_exposure = {};
      if (customExposure.amount_g) meta.custom_exposure.amount_g = parseFloat(customExposure.amount_g);
      if (customExposure.frequency) meta.custom_exposure.frequency = parseFloat(customExposure.frequency);
      if (customExposure.retention_factor) meta.custom_exposure.retention_factor = parseFloat(customExposure.retention_factor);
      if (customExposure.body_weight) meta.custom_exposure.body_weight = parseFloat(customExposure.body_weight);
      if (customExposure.ingestion_factor) meta.custom_exposure.ingestion_factor = parseFloat(customExposure.ingestion_factor);
      if (customExposure.exposure_route) meta.custom_exposure.exposure_route = customExposure.exposure_route;
    }
    return meta;
  };

  const handleSubmit = async (mode) => {
    setError('');
    if (!validateBase(mode)) return;
    setLoading(mode);

    try {
      let response;
      const metadata = buildMetadata();

      if (uploadMethod === 'file') {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('product_name', productName);
        fd.append('product_type', metadata.product_type);
        fd.append('target_population', metadata.target_population);
        fd.append('application_area', metadata.application_area);
        fd.append('frequency_of_use', metadata.frequency_of_use);
        fd.append('assessment_mode', mode);
        response = await axios.post(`${API_URL}/api/formulations/upload`, fd, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        const validIngredients = ingredients
          .filter(i => i.inci_name.trim() && i.concentration)
          .map(i => ({
            inci_name: i.inci_name.trim(),
            cas_number: i.cas_number.trim() || null,
            concentration: parseFloat(i.concentration),
            function: i.function.trim() || null
          }));
        response = await axios.post(`${API_URL}/api/formulations`, {
          metadata, ingredients: validIngredients, assessment_mode: mode,
          fragrance: fragrance.contains_fragrance ? {
            contains_fragrance: true,
            fragrance_disclosed: fragrance.fragrance_disclosed,
            fragrance_pct_in_product: fragrance.fragrance_pct_in_product,
            fragrance_components: fragrance.fragrance_disclosed
              ? (fragrance.fragrance_components || [])
                  .filter(c => c.inci_name?.trim() && c.concentration)
                  .map(c => ({ inci_name: c.inci_name.trim(), cas_number: c.cas_number?.trim() || null, concentration: parseFloat(c.concentration) }))
              : [],
            ifra_certificate_uploaded: fragrance.ifra_certificate_uploaded
          } : null
        }, { withCredentials: true });
      }

      // Upload supporting documents
      if (supportDocs.length > 0 && response.data.id) {
        const docFd = new FormData();
        supportDocs.forEach(doc => docFd.append('files', doc));
        docFd.append('formulation_id', response.data.id);
        docFd.append('document_type', 'general');
        try {
          await axios.post(`${API_URL}/api/documents/upload-multiple`, docFd, {
            withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (docErr) { console.warn('Document upload failed:', docErr); }
      }

      navigate(`/assessment/${response.data.id}`);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Error processing formulation');
    } finally {
      setLoading(false);
    }
  };

  const groupOrder = ['Cosmetics', 'Personal Care', 'Consumer Products', 'Pet Products', 'Specialty'];

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-[#111827]">New Assessment</h1>
          <p className="text-[#4B5563] mt-1">Select product type, upload ingredients, then choose assessment depth</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-[#FDE8E8] border border-[#F8B4B4] rounded-sm">
            <p className="text-sm text-[#9B1C1C]" data-testid="upload-error">{error}</p>
          </div>
        )}

        <div className="space-y-8">
          {/* STEP 1: Product Name */}
          <div className="card-flat p-6">
            <h2 className="text-lg font-semibold text-[#111827] mb-4">Product Name</h2>
            <Input type="text" value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="e.g., Ultra Repair Cream" required data-testid="product-name-input" className="h-11 rounded-sm border-gray-200 focus:ring-2 focus:ring-blue-600" />
          </div>

          {/* STEP 2: Product Type Selection */}
          <div className="card-flat p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold text-[#111827]">Select Product Type</h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild><Info size={18} className="text-[#9CA3AF] cursor-help" /></TooltipTrigger>
                  <TooltipContent><p className="max-w-xs text-sm">Product type controls exposure defaults (amount, frequency, retention factor, body weight) used in Full Assessment calculations.</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm text-[#4B5563] mb-5">Required for Full Assessment. Controls exposure modeling and IFRA mapping.</p>

            {categories ? (
              <div className="space-y-6">
                {groupOrder.map(group => {
                  const items = categories.categories[group];
                  if (!items) return null;
                  return (
                    <div key={group}>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-3">{group}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {items.map(cat => {
                          const Icon = CATEGORY_ICONS[cat.id] || FlaskIcon;
                          const isSelected = selectedCategory === cat.id;
                          return (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => { setSelectedCategory(cat.id); setError(''); }}
                              className={`text-left p-3 border-2 rounded-sm transition-all ${
                                isSelected
                                  ? 'border-[#0B2447] bg-[#0B2447]/5'
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                              }`}
                              data-testid={`product-category-${cat.id}`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Icon size={18} weight={isSelected ? 'duotone' : 'regular'} className={isSelected ? 'text-[#0B2447]' : 'text-[#4B5563]'} />
                                <span className={`text-xs font-semibold truncate ${isSelected ? 'text-[#0B2447]' : 'text-[#111827]'}`}>
                                  {cat.label.split(' (')[0].split(' / ')[0]}
                                </span>
                              </div>
                              {cat.ifra_category && (
                                <span className="text-[10px] text-[#4B5563] font-mono">IFRA {cat.ifra_category}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="skeleton h-16 rounded-sm" />)}
              </div>
            )}

            {/* Selected Product Exposure Defaults */}
            {selectedCatData && (
              <div className="mt-5 p-4 bg-[#F9FAFB] border border-gray-200 rounded-sm" data-testid="exposure-defaults-panel">
                {/* Pet species/size configurator — shown only for Pet Products */}
                {selectedCatData.pet_product && (
                  <div className="mb-4 p-3 bg-white border border-[#F3CC64] rounded-sm" data-testid="pet-configurator">
                    <div className="flex items-center gap-2 mb-2">
                      <PawPrint size={16} className="text-[#723B13]" weight="duotone" />
                      <p className="text-xs font-bold uppercase tracking-wider text-[#723B13]">Pet Product — Species Profile</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold">Species</Label>
                        <Select value={petSpecies} onValueChange={setPetSpecies}>
                          <SelectTrigger className="h-9 text-sm rounded-sm mt-1" data-testid="upload-pet-species-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dog">Dog</SelectItem>
                            <SelectItem value="cat">Cat (elevated grooming exposure)</SelectItem>
                            <SelectItem value="horse">Horse</SelectItem>
                            <SelectItem value="rabbit">Rabbit</SelectItem>
                            <SelectItem value="bird">Bird (companion)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold">Size</Label>
                        <Select value={petSize} onValueChange={setPetSize}>
                          <SelectTrigger className="h-9 text-sm rounded-sm mt-1" data-testid="upload-pet-size-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="small">Small</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="large">Large</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {petSpecies === 'cat' && (
                      <p className="text-[11px] text-[#9B1C1C] mt-2 flex items-center gap-1" data-testid="upload-cat-warning">
                        <Warning size={12} weight="fill" /> Cats groom extensively and have deficient glucuronidation — oral exposure and metabolic sensitivity are elevated.
                      </p>
                    )}
                    <p className="text-[11px] text-[#4B5563] mt-2">
                      After running Full Assessment, a species-specific pet screen is automatically performed and appended to the report.
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-[#111827]">
                    Exposure Defaults — {selectedCatData.label}
                  </p>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="custom-toggle" className="text-xs text-[#4B5563]">
                      {useCustomExposure ? 'Custom Scenario' : 'Aᴳ Standard Defaults'}
                    </Label>
                    <Switch
                      id="custom-toggle"
                      checked={useCustomExposure}
                      onCheckedChange={setUseCustomExposure}
                      data-testid="custom-exposure-toggle"
                    />
                  </div>
                </div>

                {!useCustomExposure ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Amount</p>
                      <p className="text-sm font-mono font-semibold text-[#111827]">{selectedCatData.amount_g} g/use</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Frequency</p>
                      <p className="text-sm font-mono font-semibold text-[#111827]">{selectedCatData.frequency}x/day</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Retention</p>
                      <p className="text-sm font-mono font-semibold text-[#111827]">{selectedCatData.retention_factor}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Body Weight</p>
                      <p className="text-sm font-mono font-semibold text-[#111827]">{selectedCatData.body_weight} kg</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Route</p>
                      <p className="text-sm font-semibold text-[#111827] capitalize">{selectedCatData.exposure_route}</p>
                    </div>
                    {selectedCatData.ifra_category && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">IFRA Category</p>
                        <p className="text-sm font-mono font-semibold text-[#111827]">{selectedCatData.ifra_category}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Amount (g/use)</Label>
                      <Input type="number" step="0.01" placeholder={String(selectedCatData.amount_g)} value={customExposure.amount_g} onChange={e => setCustomExposure({...customExposure, amount_g: e.target.value})} data-testid="custom-amount-input" className="h-9 text-sm rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Frequency (/day)</Label>
                      <Input type="number" step="0.1" placeholder={String(selectedCatData.frequency)} value={customExposure.frequency} onChange={e => setCustomExposure({...customExposure, frequency: e.target.value})} data-testid="custom-frequency-input" className="h-9 text-sm rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Retention Factor</Label>
                      <Input type="number" step="0.001" min="0" max="1" placeholder={String(selectedCatData.retention_factor)} value={customExposure.retention_factor} onChange={e => setCustomExposure({...customExposure, retention_factor: e.target.value})} data-testid="custom-retention-input" className="h-9 text-sm rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Body Weight (kg)</Label>
                      <Input type="number" step="0.1" placeholder={String(selectedCatData.body_weight)} value={customExposure.body_weight} onChange={e => setCustomExposure({...customExposure, body_weight: e.target.value})} data-testid="custom-bodyweight-input" className="h-9 text-sm rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Exposure Route</Label>
                      <Select value={customExposure.exposure_route} onValueChange={v => setCustomExposure({...customExposure, exposure_route: v})}>
                        <SelectTrigger className="h-9 text-sm rounded-sm" data-testid="custom-route-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dermal">Dermal</SelectItem>
                          <SelectItem value="oral">Oral</SelectItem>
                          <SelectItem value="inhalation">Inhalation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Ingestion Factor</Label>
                      <Input type="number" step="0.01" min="0" max="1" placeholder={String(selectedCatData.ingestion_factor || 0)} value={customExposure.ingestion_factor} onChange={e => setCustomExposure({...customExposure, ingestion_factor: e.target.value})} data-testid="custom-ingestion-input" className="h-9 text-sm rounded-sm" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* STEP 3: Ingredients (file or manual) */}
          <div className="card-flat">
            <div className="flex border-b border-gray-200">
              <button type="button" onClick={() => setUploadMethod('file')} className={`flex-1 py-3 px-4 text-sm font-semibold transition-colors ${uploadMethod === 'file' ? 'text-[#0B2447] border-b-2 border-[#0B2447]' : 'text-[#4B5563] hover:text-[#111827]'}`} data-testid="file-upload-tab">
                <UploadSimple size={18} className="inline mr-2" /> File Upload
              </button>
              <button type="button" onClick={() => setUploadMethod('manual')} className={`flex-1 py-3 px-4 text-sm font-semibold transition-colors ${uploadMethod === 'manual' ? 'text-[#0B2447] border-b-2 border-[#0B2447]' : 'text-[#4B5563] hover:text-[#111827]'}`} data-testid="manual-entry-tab">
                <Plus size={18} className="inline mr-2" /> Manual Entry
              </button>
            </div>
            <div className="p-6">
              {uploadMethod === 'file' ? (
                <div>
                  <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors ${dragActive ? 'border-[#2563EB] bg-[#E1EFFE]' : 'border-gray-300 hover:border-gray-400'}`}>
                    {file ? (
                      <div className="flex items-center justify-center gap-3">
                        <File size={32} className="text-[#2563EB]" />
                        <div className="text-left">
                          <p className="font-semibold text-[#111827]">{file.name}</p>
                          <p className="text-sm text-[#4B5563]">{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button type="button" onClick={() => setFile(null)} className="p-1 hover:bg-gray-100 rounded" data-testid="remove-file-btn"><X size={20} className="text-[#4B5563]" /></button>
                      </div>
                    ) : (
                      <>
                        <UploadSimple size={48} className="mx-auto text-[#9CA3AF] mb-4" />
                        <p className="text-[#111827] font-semibold mb-2">Drop your formulation file here</p>
                        <p className="text-sm text-[#4B5563] mb-4">CSV or Excel with INCI Name and % columns</p>
                        <label>
                          <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" data-testid="file-input" />
                          <span className="btn-secondary inline-flex items-center px-4 py-2 cursor-pointer">Browse Files</span>
                        </label>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="space-y-3">
                    {ingredients.map((ingredient, index) => (
                      <div key={ingredient._id} className="flex gap-3 items-start">
                        <div className="flex-1 grid grid-cols-4 gap-3">
                          <Input placeholder="INCI Name *" value={ingredient.inci_name} onChange={(e) => updateIngredient(index, 'inci_name', e.target.value)} data-testid={`ingredient-name-${index}`} className="h-10 rounded-sm text-sm" />
                          <Input placeholder="CAS Number" value={ingredient.cas_number} onChange={(e) => updateIngredient(index, 'cas_number', e.target.value)} data-testid={`ingredient-cas-${index}`} className="h-10 rounded-sm text-sm" />
                          <Input type="number" placeholder="% *" step="0.001" min="0" max="100" value={ingredient.concentration} onChange={(e) => updateIngredient(index, 'concentration', e.target.value)} data-testid={`ingredient-concentration-${index}`} className="h-10 rounded-sm text-sm tabular-nums" />
                          <Input placeholder="Function" value={ingredient.function} onChange={(e) => updateIngredient(index, 'function', e.target.value)} data-testid={`ingredient-function-${index}`} className="h-10 rounded-sm text-sm" />
                        </div>
                        <button type="button" onClick={() => removeIngredient(index)} disabled={ingredients.length === 1} className="p-2 text-[#4B5563] hover:text-[#9B1C1C] hover:bg-red-50 rounded disabled:opacity-30" data-testid={`remove-ingredient-${index}`}><Trash size={18} /></button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" onClick={addIngredient} className="mt-4 rounded-sm" data-testid="add-ingredient-btn"><Plus size={18} className="mr-2" /> Add Ingredient</Button>

                  {/* Live Hazard Preview — real-time screening as ingredients change */}
                  <LiveHazardPreview ingredients={ingredients} />
                </div>
              )}
            </div>
          </div>

          {/* STEP 4: Safety Claims */}
          <ClaimSelectionSection
            safetyClaims={safetyClaims}
            setSafetyClaims={setSafetyClaims}
            hasClaims={hasClaims}
            setHasClaims={setHasClaims}
          />

          {/* STEP 5: Fragrance Module */}
          <FragranceInputSection fragrance={fragrance} setFragrance={setFragrance} />

          {/* STEP 5: Supporting Documents */}
          <div className="card-flat p-6">
            <h2 className="text-lg font-semibold text-[#111827] mb-1">Supporting Documents</h2>
            <p className="text-sm text-[#4B5563] mb-4">Attach SDS, certificates, or other files (max 25 MB each)</p>
            {supportDocs.length > 0 && (
              <div className="space-y-2 mb-4">
                {supportDocs.map((doc, idx) => (
                  <div key={doc.name + doc.size} className="flex items-center justify-between p-3 bg-[#F3F4F6] rounded-sm">
                    <div className="flex items-center gap-2">
                      <File size={18} className="text-[#4B5563]" />
                      <span className="text-sm font-medium text-[#111827]">{doc.name}</span>
                      <span className="text-xs text-[#4B5563]">({(doc.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button type="button" onClick={() => removeSupportDoc(idx)} className="p-1 hover:bg-gray-200 rounded" data-testid={`remove-support-doc-${idx}`}><X size={16} className="text-[#4B5563]" /></button>
                  </div>
                ))}
              </div>
            )}
            <label>
              <input type="file" multiple onChange={handleSupportDocSelect} className="hidden" data-testid="support-doc-input" />
              <span className="btn-secondary inline-flex items-center px-4 py-2 cursor-pointer text-sm"><Plus size={16} className="mr-2" /> Add Documents</span>
            </label>
          </div>

          {/* STEP 6: Project Type — six distinct ToxIntel workflows */}
          <div className="card-flat p-6">
            <h2 className="text-lg font-semibold text-[#111827] mb-2">Select Project Type</h2>
            <p className="text-sm text-[#4B5563] mb-6">Six enterprise workflows · each with a distinct output type. No overlap.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button type="button" onClick={() => handleSubmit('hazard')} disabled={!!loading}
                className="group relative text-left p-5 border-2 border-[#2563EB] rounded-sm bg-white hover:bg-[#E1EFFE] transition-all disabled:opacity-60"
                data-testid="run-hazard-screening-btn">
                <div className="flex items-center gap-3 mb-2">
                  <Lightning size={22} className="text-[#2563EB]" weight="duotone" />
                  <h3 className="text-sm font-bold text-[#0B2447]"><TI color="#E63946" /> Risk Screen</h3>
                </div>
                <p className="text-xs text-[#4B5563]">Scientific Risk Memo · contaminant surveillance · escalation pathway · 3 business days.</p>
                {loading === 'hazard' && <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-sm"><SpinnerGap size={24} className="text-[#2563EB] animate-spin" /></div>}
              </button>

              <button type="button" onClick={() => handleSubmit('tra_qra')} disabled={!!loading}
                className="group relative text-left p-5 border-2 border-[#723B13] rounded-sm bg-white hover:bg-[#FEF3C7] transition-all disabled:opacity-60"
                data-testid="run-tra-qra-btn">
                <div className="flex items-center gap-3 mb-2">
                  <Calculator size={22} className="text-[#723B13]" weight="duotone" />
                  <h3 className="text-sm font-bold text-[#0B2447]">TRA / QRA</h3>
                </div>
                <p className="text-xs text-[#4B5563]">Exposure-based QRA · route-specific SED · NOAEL/POD/BMDL · MoS · risk characterisation.</p>
                {loading === 'tra_qra' && <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-sm"><SpinnerGap size={24} className="text-[#723B13] animate-spin" /></div>}
              </button>

              <button type="button" onClick={() => handleSubmit('full')} disabled={!!loading}
                className="group relative text-left p-5 border-2 border-[#0B2447] rounded-sm bg-[#0B2447] hover:bg-[#163868] transition-all disabled:opacity-60"
                data-testid="run-full-assessment-btn">
                <div className="flex items-center gap-3 mb-2">
                  <Microscope size={22} className="text-white" weight="duotone" />
                  <h3 className="text-sm font-bold text-white">Full Toxicological Assessment</h3>
                </div>
                <p className="text-xs text-white/70">EC 1223-Style Part A + Part B · Aᴳ-signed CPSR-aligned dossier.</p>
                {loading === 'full' && <div className="absolute inset-0 bg-[#0B2447]/80 flex items-center justify-center rounded-sm"><SpinnerGap size={24} className="text-white animate-spin" /></div>}
              </button>

              <button type="button" onClick={() => handleSubmit('prop65')} disabled={!!loading}
                className="group relative text-left p-5 border-2 border-[#9B1C1C] rounded-sm bg-white hover:bg-[#FEE2E2] transition-all disabled:opacity-60"
                data-testid="run-prop65-btn">
                <div className="flex items-center gap-3 mb-2">
                  <Scales size={22} className="text-[#9B1C1C]" weight="duotone" />
                  <h3 className="text-sm font-bold text-[#0B2447]">Prop 65</h3>
                </div>
                <p className="text-xs text-[#4B5563]">California Prop 65 · NSRL/MADL derivation support · warning determination rationale.</p>
                {loading === 'prop65' && <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-sm"><SpinnerGap size={24} className="text-[#9B1C1C] animate-spin" /></div>}
              </button>

              <button type="button" onClick={() => handleSubmit('sds')} disabled={!!loading}
                className="group relative text-left p-5 border-2 border-[#374151] rounded-sm bg-white hover:bg-gray-100 transition-all disabled:opacity-60"
                data-testid="run-sds-btn">
                <div className="flex items-center gap-3 mb-2">
                  <FileDoc size={22} className="text-[#374151]" weight="duotone" />
                  <h3 className="text-sm font-bold text-[#0B2447]">SDS Generator</h3>
                </div>
                <p className="text-xs text-[#4B5563]">OSHA/GHS 16-Section Safety Data Sheet · same-day.</p>
                {loading === 'sds' && <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-sm"><SpinnerGap size={24} className="text-[#374151] animate-spin" /></div>}
              </button>

              <button type="button" onClick={() => handleSubmit('claims')} disabled={!!loading}
                className="group relative text-left p-5 border-2 border-[#0E9F6E] rounded-sm bg-white hover:bg-[#DEF7EC] transition-all disabled:opacity-60"
                data-testid="run-claims-btn">
                <div className="flex items-center gap-3 mb-2">
                  <Certificate size={22} className="text-[#0E9F6E]" weight="duotone" />
                  <h3 className="text-sm font-bold text-[#0B2447]">Claims Support</h3>
                </div>
                <p className="text-xs text-[#4B5563]">Pregnancy-safe · kids/teens · sensitive skin · hypoallergenic · HRIPT/RIPT guidance.</p>
                {loading === 'claims' && <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-sm"><SpinnerGap size={24} className="text-[#0E9F6E] animate-spin" /></div>}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <Link to="/dashboard"><Button type="button" variant="outline" className="rounded-sm h-11 px-6">Cancel</Button></Link>
          </div>
        </div>
      </main>
    </div>
  );
}
